// ─────────────────────────────────────────────────────────────────────────
// DocumentosCard — genera CONTRATO y FINIQUITO en PDF desde la ficha del
// trabajador (27-ago-2026). Backend: services/documentosRrhh.js (plantillas
// del abogado). Ambos PDF salen como BORRADOR: la causal y los montos finales
// los decide una persona, nunca el sistema.
// ─────────────────────────────────────────────────────────────────────────
import React, { useState } from "react";
import backend from "../../backend";

const fmt = n => `$${new Intl.NumberFormat("es-CL").format(Math.round(n || 0))}`;

const CAUSALES = [
  ["art159_1", "Mutuo acuerdo de las partes"],
  ["art159_2", "Renuncia del trabajador"],
  ["art159_4", "Vencimiento del plazo convenido"],
  ["art159_5", "Conclusión de la obra o faena"],
  ["art159_6", "Caso fortuito o fuerza mayor"],
  ["art160", "Causal imputable al trabajador (art. 160)"],
  ["art161", "Necesidades de la empresa"],
];

const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  input: { background: "#161616", border: "1px solid #333", borderRadius: 8, padding: "9px 11px", fontSize: 13, color: "#ddd", width: "100%", boxSizing: "border-box" },
  btn:   { minHeight: 44, borderRadius: 10, border: "none", background: "#0d2035", color: "#3498db", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "0 16px" },
};

const abrirPdf = async (url, params) => {
  const w = window.open("", "_blank"); // antes del await (iOS)
  try {
    const res = await backend.get(url, { params, responseType: "blob" });
    const blob = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    if (w) w.location = blob; else window.location = blob;
    setTimeout(() => window.URL.revokeObjectURL(blob), 60000);
  } catch (e) {
    if (w) w.close();
    let msg = e.message;
    try { msg = JSON.parse(await e.response?.data?.text?.() || "{}").error || msg; } catch { /* blob */ }
    alert("No se pudo generar el PDF: " + msg);
  }
};

const DocumentosCard = ({ trabajadorId }) => {
  const [causal, setCausal] = useState("art161");
  const [numeral, setNumeral] = useState("7");
  const [fechaTermino, setFechaTermino] = useState("");
  const [preview, setPreview] = useState(null);
  const [cargando, setCargando] = useState(false);

  if (!trabajadorId) return null;

  const verPreview = async () => {
    if (!fechaTermino) { alert("Indica la fecha de término"); return; }
    setCargando(true);
    try {
      const r = await backend.post(`documento-rrhh/finiquito/${trabajadorId}/preview`, {
        causal, causalNumeral: causal === "art160" ? Number(numeral) : undefined, fechaTermino,
      });
      setPreview(r.data);
    } catch (e) { alert(e?.response?.data?.error || e.message); }
    setCargando(false);
  };

  const paramsFiniquito = { causal, fechaTermino, ...(causal === "art160" ? { causalNumeral: numeral } : {}) };

  return (
    <div style={{ ...S.card, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>📄 Documentos laborales</div>

      {/* Contrato */}
      <button style={{ ...S.btn, border: "1px solid #1a4a6e" }}
        onClick={() => abrirPdf(`documento-rrhh/contrato/${trabajadorId}/pdf`)}>
        📄 Contrato de trabajo (PDF borrador)
      </button>

      {/* Finiquito */}
      <div style={{ borderTop: "1px solid #2e2e2e", paddingTop: 12 }}>
        <div style={{ ...S.label, marginBottom: 8, color: "#e74c3c" }}>Finiquito</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <div>
            <div style={{ ...S.label, marginBottom: 4 }}>Causal legal</div>
            <select value={causal} onChange={e => { setCausal(e.target.value); setPreview(null); }} style={S.input}>
              {CAUSALES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <div style={{ ...S.label, marginBottom: 4 }}>Fecha de término</div>
            <input type="date" value={fechaTermino} onChange={e => { setFechaTermino(e.target.value); setPreview(null); }} style={S.input} />
          </div>
        </div>
        {causal === "art160" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ ...S.label, marginBottom: 4 }}>Numeral art. 160</div>
            <select value={numeral} onChange={e => setNumeral(e.target.value)} style={S.input}>
              {[1,2,3,4,5,6,7].map(n => <option key={n} value={n}>N° {n}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={{ ...S.btn, background: "#2a2a2a", color: "#ccc", border: "1px solid #3a3a3a", flex: 1 }}
            onClick={verPreview} disabled={cargando}>
            {cargando ? "Calculando…" : "Ver desglose"}
          </button>
          <button style={{ ...S.btn, background: "#2d1515", color: "#e74c3c", border: "1px solid #4a2020", flex: 1 }}
            onClick={() => abrirPdf(`documento-rrhh/finiquito/${trabajadorId}/pdf`, paramsFiniquito)}>
            📄 Finiquito (PDF borrador)
          </button>
        </div>

        {preview && preview.totales && (
          <div style={{ marginTop: 10, background: "#1f1f1f", border: "1px solid #2e2e2e", borderRadius: 8, padding: 12 }}>
            {(preview.lineas || []).map((h, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#b8b8b8", padding: "3px 0" }}>
                <span>{h.glosa}</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(h.monto)}</span>
              </div>
            ))}
            {(preview.descuentos || []).map((d, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#e07a7a", padding: "3px 0" }}>
                <span>{d.glosa}</span><span style={{ fontVariantNumeric: "tabular-nums" }}>−{fmt(d.monto)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 800, color: "#2ecc71", borderTop: "1px solid #2e2e2e", marginTop: 6, paddingTop: 8 }}>
              <span>Total a pagar</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(preview.totales.total)}</span>
            </div>
            {(preview.advertencias || []).map((a, i) => (
              <div key={i} style={{ fontSize: 11, color: "#f39c12", marginTop: 6 }}>⚠ {a}</div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 8 }}>
          Borrador sobre plantilla del abogado — la causal y montos finales los decide una persona; el finiquito se ratifica ante ministro de fe.
        </div>
      </div>
    </div>
  );
};

export default DocumentosCard;
